import * as THREE from 'three/webgpu';
import { shapeCircle } from 'three/tsl';
import { CameraSpriteNodeMaterial } from './CameraSpriteNodeMaterial';


/**
 * 旋转中心拓扑标记材质类型。
 *
 * 设计说明：
 * 1. 旋转中心调试只会高亮点、线、面三类拓扑代理；
 * 2. 使用枚举集中表达有限类型，避免调用方用字符串拼接材质语义；
 * 3. 材质名称会写入 three.js 对象，方便调试时直接识别高亮来源。
 */
export enum RotationCenterTopologyMarkerMaterialKind {
    /** 拓扑点标记材质。 */
    FeaturePoint = 'feature-point',
    /** 拓扑线标记材质。 */
    FeatureLine = 'feature-line',
    /** 拓扑面标记材质。 */
    FeatureFace = 'feature-face',
}

/** 旋转中心拓扑面标记透明度。 */
const ROTATION_CENTER_TOPOLOGY_FACE_OPACITY = 0.68;

/** 旋转中心拓扑线标记线宽。 */
const ROTATION_CENTER_TOPOLOGY_LINE_WIDTH = 3;

/**
 * 创建旋转中心拓扑面标记材质。
 *
 * 设计说明：
 * 1. 面标记属于临时调试反馈，不参与实体真实着色；
 * 2. 关闭深度测试可以确保靠近屏幕中心的面被稳定染色，避免被自身实体面片遮挡；
 * 3. 每次创建独占材质，释放时由高亮控制器统一销毁。
 *
 * @param color 标记颜色。
 * @returns 拓扑面标记材质。
 */
export function createRotationCenterTopologyFaceMarkerMaterial(
    color: THREE.ColorRepresentation,
): THREE.MeshBasicMaterial {
    return new THREE.MeshBasicMaterial({
        name: `旋转中心-${RotationCenterTopologyMarkerMaterialKind.FeatureFace}-标记材质`,
        color,
        transparent: true,
        opacity: ROTATION_CENTER_TOPOLOGY_FACE_OPACITY,
        depthTest: false,
        depthWrite: false,
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
    });
}

/**
 * 创建旋转中心拓扑线标记材质。
 *
 * @param color 标记颜色。
 * @returns 拓扑线标记材质。
 */
export function createRotationCenterTopologyLineMarkerMaterial(
    color: THREE.ColorRepresentation,
): THREE.LineBasicMaterial {
    return new THREE.LineBasicMaterial({
        name: `旋转中心-${RotationCenterTopologyMarkerMaterialKind.FeatureLine}-标记材质`,
        color,
        linewidth: ROTATION_CENTER_TOPOLOGY_LINE_WIDTH,
        transparent: false,
        opacity: 1,
        depthTest: false,
        depthWrite: false,
    });
}

/**
 * 创建旋转中心拓扑点标记材质。
 *
 * 设计说明：
 * 1. 点标记沿用项目统一的屏幕空间圆形 Sprite 材质；
 * 2. 关闭深度测试，保证局部细节附近的拓扑点不会被模型面片遮住；
 * 3. 该材质由旋转中心高亮控制器独占，不能放入全局点材质缓存。
 *
 * @param color 标记颜色。
 * @returns 拓扑点标记材质。
 */
export function createRotationCenterTopologyPointMarkerMaterial(
    color: THREE.ColorRepresentation,
): CameraSpriteNodeMaterial {
    const material = new CameraSpriteNodeMaterial({
        sizeAttenuation: false,
        alphaToCoverage: true,
        transparent: true,
        opacityNode: shapeCircle(),
        color,
    });

    // 点标记是临时前景反馈，必须稳定覆盖在实体表面之上。
    material.name = `旋转中心-${RotationCenterTopologyMarkerMaterialKind.FeaturePoint}-标记材质`;
    material.transparent = true;
    material.opacity = 1;
    material.depthTest = false;
    material.depthWrite = false;
    return material;
}

